export type PlanningCycleStatus =
  | 'draft'
  | 'open'
  | 'in_review'
  | 'approved'
  | 'closed'
  | 'cancelled';

export interface PlanningCycleRow {
  id: number;
  name: string;
  cycle_type: string;
  status: PlanningCycleStatus;
  period_start: string;
  period_end: string;
  forecast_horizon_start: string;
  forecast_horizon_end: string;
}

export interface CreatePlanningCyclePayload {
  name: string;
  cycle_type: string;
  period_start: string;
  period_end: string;
  version_name: string;
}

export type AssumptionImpactMode = 'auto' | 'manual';

export interface ManualAssumptionImpactInput {
  family_codes: string[];
  subfamily_codes: string[];
  product_codes: string[];
  all_plan_families?: boolean;
  warehouse_codes: string[];
  supplier_codes: string[];
  time_bucket: string;
  impact_type: 'percentage' | 'absolute_qty';
  impact_percentage: string;
  impact_qty: string;
  notes: string;
}

export interface PlanScopeFamily {
  id: number;
  client_code: string;
  name: string;
}

export interface AssumptionImpactDetail {
  id: number;
  family: number | null;
  subfamily: number | null;
  product: number | null;
  warehouse: number | null;
  supplier: number | null;
  time_bucket: string;
  impact_type: string;
  impact_percentage: string | null;
  impact_qty: string | null;
  notes: string;
}

export interface AssumptionDetail {
  id: number;
  title: string;
  description: string;
  assumption_types: string[];
  assumption_type: string;
  severity: string;
  probability: number;
  start_bucket: string;
  end_bucket: string;
  impact_mode: AssumptionImpactMode;
  impacts: AssumptionImpactDetail[];
}

export interface CreateAssumptionPayload {
  title: string;
  description: string;
  assumption_types: string[];
  assumption_type: string;
  start_bucket: string;
  end_bucket: string;
  severity: string;
  probability: number;
  impact_mode: AssumptionImpactMode;
  impacts_input: Array<{
    family?: number;
    subfamily?: number;
    product?: number;
    warehouse?: number | null;
    supplier?: number | null;
    time_bucket: string;
    impact_type: string;
    impact_percentage?: number | null;
    impact_qty?: number | null;
    notes?: string;
  }>;
}

export type UpdateAssumptionPayload = CreateAssumptionPayload;

export interface ConsensusAssumptionRow {
  id: number;
  title: string;
  description: string;
  status: string;
  created_at: string | null;
  assumption_type: string;
  assumption_types?: string[];
  severity: string;
  probability: number;
  start_bucket: string;
  end_bucket: string;
  impact_mode?: AssumptionImpactMode;
  impacts_count?: number;
  impact_qty?: number;
  impact_pct?: number | null;
  impact_value?: number;
  base_forecast_qty?: number;
  owner_email: string | null;
  flags?: {
    opportunity: boolean;
    risk: boolean;
    alert: boolean;
    escalate: boolean;
  };
  comments?: string;
}

export interface AssumptionsImpactSummary {
  assumption_count: number;
  base_forecast_qty: number;
  impact_qty: number;
  impact_pct: number | null;
  base_forecast_value: number;
  impact_value: number;
}

export type ConsensusChartHistoryRange = '52_weeks' | 'ytd';

export interface ConsensusDemandPoint {
  time_bucket: string;
  actual_qty: number | null;
  baseline_qty: number | null;
  open_orders_qty?: number | null;
  assumption_impact_qty: number | null;
  manual_adjustment_qty?: number | null;
  consensus_qty: number | null;
}

export interface DemandPlanChartPayload {
  granularity: string;
  history_range: string;
  current_period: string;
  current_week: string;
  history_start: string;
  history_weeks: number;
  series: ConsensusDemandPoint[];
}

export interface DemandPlanStatus {
  is_published: boolean;
  published_at: string | null;
  selected_scenario_id: number | null;
  selected_scenario_name: string | null;
  source: 'base' | 'scenario';
  editable_week_start?: string;
  can_republish?: boolean;
}

export interface ConsensusOverviewPayload {
  planning_cycle: {
    id: number;
    name: string;
    status: PlanningCycleStatus;
    period_start: string;
    period_end: string;
  };
  plan_version: {
    id: number;
    version_name: string;
    version_number: number;
    is_current: boolean;
  };
  demand_plan?: DemandPlanStatus;
  assumptions: ConsensusAssumptionRow[];
  assumptions_impact_summary?: AssumptionsImpactSummary;
  plan_scope?: {
    families: PlanScopeFamily[];
  };
  demand_plan_chart?: DemandPlanChartPayload;
  scenario_comparison?: ConsensusScenarioComparison;
}

export type ScenarioType =
  | 'optimistic'
  | 'conservative'
  | 'promotion'
  | 'supply_constraint'
  | 'custom';

export interface CreateScenarioPayload {
  name: string;
  scenario_type: ScenarioType;
  description?: string;
  parameters_json: { multiplier?: number };
}

export interface ConsensusScenarioListItem {
  id: number;
  name: string;
  scenario_type: string;
  status: string;
}

export interface ConsensusScenarioCell {
  period: string;
  demand_qty: number;
  delta_qty?: number;
  delta_pct: number;
  tone: string;
  base_qty?: number;
  prior_year_qty?: number;
  vs_prior_year_pct?: number;
  demand_value?: number;
  base_value?: number;
  delta_value?: number;
}

export interface ConsensusScenarioRow {
  scenario_id: number | null;
  scenario_name: string;
  scenario_type: string;
  status: string;
  macro_cells?: ConsensusScenarioCell[];
  cells: ConsensusScenarioCell[];
  quarter_totals?: Record<string, number>;
}

export interface ConsensusScenarioComparison {
  available_scenarios: ConsensusScenarioListItem[];
  macro_columns: Array<{
    key: string;
    granularity?: string;
    label?: string;
    horizon_start?: string;
    horizon_end?: string;
  }>;
  macro_periods: string[];
  periods: string[];
  base_row: ConsensusScenarioRow;
  scenarios: ConsensusScenarioRow[];
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export type PublishFlowStepStatus = 'pending' | 'running' | 'done' | 'error' | 'skipped';

export type PublishFlowStepId = 'validate' | 'publish' | 'replenishment' | 'supply_cache';

export interface PublishFlowStep {
  id: PublishFlowStepId;
  status: PublishFlowStepStatus;
  errorMessage?: string;
}

export const PUBLISH_FLOW_STEP_IDS: PublishFlowStepId[] = [
  'validate',
  'publish',
  'replenishment',
  'supply_cache',
];
